import React, { useState, useEffect, useMemo, useRef } from "react";
import * as api from "../api";

const fmt = (n, dec = 2) => (n == null || isNaN(n) ? "—" : Number(n).toFixed(dec));
const fmtCr = (n) => (n == null || isNaN(n) ? "—" : `₹${Number(n).toFixed(2)} Cr`);

/**
 * Multi-select dropdown with built-in search.
 * - Trigger button shows the current label / count.
 * - Open panel has a search input + checklist with checkboxes.
 * - Empty selection means "all".
 */
function MultiSelect({ label, options, selected, onChange, getId, getLabel }) {
  const idOf = getId || ((o) => o.id);
  const labelOf = getLabel || ((o) => o.name);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selSet = new Set(selected);
  const isSel = (o) => selSet.has(idOf(o));
  const toggle = (o) => {
    const id = idOf(o);
    onChange(isSel(o) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const q = (search || "").trim().toLowerCase();
  const visible = q
    ? options.filter((o) => (labelOf(o) || "").toLowerCase().includes(q))
    : options;

  const triggerLabel = selected.length === 0
    ? "all"
    : selected.length === 1
      ? (labelOf(options.find((o) => idOf(o) === selected[0])) || selected[0])
      : `${selected.length} selected`;

  return (
    <div>
      <div className="flex justify-between items-center mb-6" style={{ flexWrap: "wrap", gap: 6 }}>
        <span className="form-label text-xxs" style={{ marginBottom: 0 }}>
          {label}
          <span className="text-xxs text-dim" style={{ marginLeft: 6, fontWeight: 400 }}>
            {selected.length === 0 ? "all" : `${selected.length} selected`}
          </span>
        </span>
        {selected.length > 0 && (
          <button type="button" className="btn-link text-xxs"
            onClick={() => onChange([])}
            style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 11 }}>
            clear
          </button>
        )}
      </div>
      <div ref={ref} style={{ position: "relative" }}>
        <button type="button" className="input select"
          onClick={() => setOpen((p) => !p)}
          style={{
            width: "100%", textAlign: "left", paddingRight: 28, cursor: "pointer",
            color: selected.length ? "var(--text-primary)" : "var(--text-muted)",
          }}>
          {triggerLabel}
        </button>
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
            zIndex: 60,
            background: "var(--bg-secondary, #15192a)",
            border: "1px solid var(--border)", borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)", padding: 8,
          }}>
            <div className="flex items-center gap-6 mb-6">
              <input type="text" autoFocus className="input input-sm"
                placeholder={`Search ${label || "options"}…`}
                value={search} onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1, padding: "4px 8px" }} />
              {selected.length > 0 && (
                <button type="button" className="btn btn-ghost btn-sm"
                  onClick={() => onChange([])}>Clear</button>
              )}
            </div>
            <div style={{ maxHeight: 240, overflowY: "auto" }}>
              {visible.length === 0 && (
                <p className="text-xxs text-dim" style={{ padding: "8px 6px" }}>No matches.</p>
              )}
              {visible.map((o) => (
                <label key={idOf(o)} className="flex items-center gap-8"
                  style={{
                    cursor: "pointer", padding: "5px 8px", borderRadius: 6,
                    background: isSel(o) ? "rgba(124,143,245,0.10)" : "transparent",
                  }}>
                  <input type="checkbox" checked={isSel(o)}
                    onChange={() => toggle(o)}
                    style={{ accentColor: "var(--accent)" }} />
                  <span className="text-xs" style={{
                    flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{labelOf(o)}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    vertical_ids: [], company_ids: [], project_ids: [], year: "",
  });
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);

  // Single fetch on mount — filtering happens client-side from here on.
  useEffect(() => {
    api.getDashboard()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const facets = data?.facets || { verticals: [], companies: [], projects: [], years: [] };
  const setF = (key, value) => setFilters((p) => ({ ...p, [key]: value }));
  const resetFilters = () => setFilters({
    vertical_ids: [], company_ids: [], project_ids: [], year: "",
  });
  const hasFilter = filters.vertical_ids.length > 0 || filters.company_ids.length > 0
    || filters.project_ids.length > 0 || !!filters.year;

  // Filter project options shown in the Project chip strip to those within
  // the chosen vertical/company so the user isn't picking unrelated rows.
  const filteredProjectOptions = useMemo(() => (facets.projects || []).filter((p) => {
    if (filters.vertical_ids.length && !filters.vertical_ids.includes(p.vertical_id)) return false;
    if (filters.company_ids.length && !filters.company_ids.includes(p.company_id)) return false;
    return true;
  }), [facets.projects, filters.vertical_ids, filters.company_ids]);

  // The visible project list after applying every active filter — drives
  // both the cards and the breakdown sections.
  const visibleProjects = useMemo(() => {
    const all = data?.projects || [];
    return all.filter((p) => {
      if (filters.vertical_ids.length && !filters.vertical_ids.includes(p.vertical_id)) return false;
      if (filters.company_ids.length && !filters.company_ids.includes(p.company_id)) return false;
      if (filters.project_ids.length && !filters.project_ids.includes(p.id)) return false;
      return true;
    });
  }, [data, filters.vertical_ids, filters.company_ids, filters.project_ids]);

  if (!data && loading) return <div className="loading-container"><div className="spinner" /></div>;
  if (!data) return <p className="text-muted">Failed to load dashboard</p>;

  // Pull the value at the chosen FY (or each project's actual year if blank).
  const valueAt = (p, byYear) => {
    const y = filters.year ? String(filters.year) : String(p.actual_year);
    return Number((byYear || {})[y] || 0);
  };

  const totalRevenue = visibleProjects.reduce((s, p) => s + valueAt(p, p.revenue_by_year), 0);
  const totalEBIDTA = visibleProjects.reduce((s, p) => s + valueAt(p, p.ebidta_by_year), 0);
  const totalPAT = visibleProjects.reduce((s, p) => s + valueAt(p, p.pat_by_year), 0);
  const ongoingCount = visibleProjects.filter((p) => p.status === "Ongoing").length;
  const totalCount = visibleProjects.length;

  const yearLabel = filters.year ? `FY ${filters.year}` : "Actual";
  const stats = [
    { label: `Total Revenue (${yearLabel})`, value: fmtCr(totalRevenue), color: "#667EEA", sub: `${totalCount} project${totalCount === 1 ? "" : "s"}` },
    { label: `Total EBIDTA (${yearLabel})`, value: fmtCr(totalEBIDTA), color: "#48BB78", sub: totalRevenue > 0 ? `${fmt((totalEBIDTA / totalRevenue) * 100, 1)}% margin` : "—" },
    { label: `Total PAT (${yearLabel})`, value: fmtCr(totalPAT), color: totalPAT >= 0 ? "#48BB78" : "#F56565", sub: totalPAT >= 0 ? "Profitable" : "Loss-making" },
    { label: "Active Projects", value: ongoingCount, color: "#ED8936", sub: `of ${totalCount} total` },
  ];

  // Year columns for the PAT trend table — derived from visible projects'
  // actual years so the strip matches what's on screen.
  const years = (() => {
    if (!visibleProjects.length) return [2025, 2026, 2027, 2028, 2029, 2030];
    const ay = Math.min(...visibleProjects.map((p) => p.actual_year || 2025));
    return [ay, ay + 1, ay + 2, ay + 3, ay + 4, ay + 5];
  })();

  return (
    <div className="fade-in">
      {/* ── Filter strip (collapsible) ── */}
      <div className="card mb-20" style={{ padding: 14 }}>
        <div className="flex justify-between items-center" style={{
              flexWrap: "wrap", gap: 8,
              marginBottom: filtersCollapsed ? 0 : 12,
            }}>
          <button type="button"
            onClick={() => setFiltersCollapsed((p) => !p)}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "transparent", border: "none", cursor: "pointer",
              padding: 0, color: "inherit", textAlign: "left", flex: 1, minWidth: 0,
            }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
              style={{
                transform: filtersCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease", flexShrink: 0,
                color: "var(--text-secondary)",
              }}>
              <path d="M6 9l6 6 6-6" />
            </svg>
            <span className="text-xs fw-600" style={{ textTransform: "uppercase", letterSpacing: 0.6, color: "var(--text-secondary)" }}>
              Filter
              {hasFilter && (
                <span className="text-xxs fw-600" style={{
                  marginLeft: 8, padding: "2px 8px", borderRadius: 12,
                  background: "rgba(124,143,245,0.18)", color: "var(--accent)",
                  border: "1px solid rgba(124,143,245,0.40)", letterSpacing: 0.4,
                }}>
                  {[
                    filters.vertical_ids.length && `${filters.vertical_ids.length} vertical${filters.vertical_ids.length === 1 ? "" : "s"}`,
                    filters.company_ids.length && `${filters.company_ids.length} compan${filters.company_ids.length === 1 ? "y" : "ies"}`,
                    filters.project_ids.length && `${filters.project_ids.length} project${filters.project_ids.length === 1 ? "" : "s"}`,
                    filters.year && `FY ${filters.year}`,
                  ].filter(Boolean).join(" · ")}
                </span>
              )}
            </span>
          </button>
          {hasFilter && !filtersCollapsed && (
            <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); resetFilters(); }}>Reset filters</button>
          )}
        </div>
        {!filtersCollapsed && (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
        }}>
          <MultiSelect
            label="Vertical"
            options={facets.verticals || []}
            selected={filters.vertical_ids}
            onChange={(ids) => setF("vertical_ids", ids)} />
          <MultiSelect
            label="Company"
            options={facets.companies || []}
            selected={filters.company_ids}
            onChange={(ids) => setF("company_ids", ids)} />
          <MultiSelect
            label="Project"
            options={filteredProjectOptions}
            selected={filters.project_ids}
            onChange={(ids) => setF("project_ids", ids)} />
          <div>
            <div className="flex justify-between items-center mb-6" style={{ flexWrap: "wrap", gap: 6 }}>
              <span className="form-label text-xxs" style={{ marginBottom: 0 }}>
                Year
                <span className="text-xxs text-dim" style={{ marginLeft: 6, fontWeight: 400 }}>
                  {filters.year ? `FY ${filters.year}` : "each project's Actual"}
                </span>
              </span>
              {filters.year && (
                <button type="button" className="btn-link text-xxs"
                  onClick={() => setF("year", "")}
                  style={{ background: "transparent", border: "none", color: "var(--text-muted)", cursor: "pointer", fontSize: 11 }}>
                  clear
                </button>
              )}
            </div>
            <select className="input select" style={{ width: "100%" }}
              value={filters.year || ""}
              onChange={(e) => setF("year", e.target.value)}>
              <option value="">Actual (each project's)</option>
              {(facets.years || []).map((y) => (
                <option key={y} value={y}>FY {y}</option>
              ))}
            </select>
          </div>
        </div>
        )}
      </div>

      <div className="grid-4 mb-24">
        {stats.map((s, i) => (
          <div key={i} className="card stat-card">
            <div className="stat-indicator" style={{ background: s.color }} />
            <p className="text-xxs text-muted" style={{ textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 600 }}>{s.label}</p>
            <p className="heading mt-8" style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.value}</p>
            <p className="text-xs text-dim mt-6">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid-2">
        {/* Revenue by project */}
        <div className="card">
          <h3 className="heading fw-600 mb-16" style={{ fontSize: 16 }}>Revenue by Project ({yearLabel})</h3>
          {visibleProjects.length === 0 && (
            <p className="text-xs text-dim" style={{ padding: 12 }}>No projects match the filters.</p>
          )}
          {visibleProjects.map((p) => {
            const rev = valueAt(p, p.revenue_by_year);
            const pct = totalRevenue > 0 ? (rev / totalRevenue) * 100 : 0;
            return (
              <div key={p.id} className="mb-16">
                <div className="flex justify-between mb-6">
                  <span className="text-sm">{p.name}</span>
                  <span className="mono text-xs text-accent">{fmtCr(rev)}</span>
                </div>
                <div className="progress-bar-bg">
                  <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* PAT Trend */}
        <div className="card">
          <h3 className="heading fw-600 mb-16" style={{ fontSize: 16 }}>PAT Trend (₹ Cr)</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Project</th>
                  {years.map((y) => <th key={y} style={{ textAlign: "right" }}>{y}</th>)}
                </tr>
              </thead>
              <tbody>
                {visibleProjects.length === 0 && (
                  <tr><td colSpan={years.length + 1} className="text-muted"
                          style={{ textAlign: "center", padding: 18 }}>
                    No projects match the filters.
                  </td></tr>
                )}
                {visibleProjects.map((p) => (
                  <tr key={p.id}>
                    <td className="fw-500">{p.name}</td>
                    {years.map((y) => {
                      const v = (p.pat_by_year || {})[String(y)];
                      return (
                        <td key={y} className={`mono text-xs ${v < 0 ? "text-neg" : "text-pos"}`} style={{ textAlign: "right" }}>
                          {fmt(v)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
